import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSettings } from "@/context/SettingsContext";
import { submitEnquiry } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";

const COUNTRIES = ["Malaysia", "Indonesia", "Singapore", "Thailand", "Vietnam", "Philippines", "Brunei", "India", "United Arab Emirates", "Saudi Arabia", "United Kingdom", "United States", "Canada", "Australia", "New Zealand", "Germany", "France", "Netherlands", "Spain", "Italy", "Japan", "South Korea", "China", "Hong Kong", "Taiwan", "South Africa", "Other"];

const empty = {
  name: "", country: "", phone: "", whatsapp: "", email: "",
  travel_dates: "", adults: 2, children: 0, preferred_tour: "", message: "",
};

export default function EnquiryModal() {
  const { open, prefill, closeEnquiry } = useEnquiry();
  const { settings } = useSettings();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...prefill });
      setDone(false);
    }
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const token = await getRecaptchaToken(settings?.recaptcha_site_key, "enquiry");
      await submitEnquiry({
        ...form,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
      }, token);
      setDone(true);
      toast.success("Enquiry sent! Our travel designer will be in touch soon.");
    } catch (err) {
      toast.error("Could not send enquiry. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="enquiry-modal"
        >
          <div className="absolute inset-0 bg-[#2d1616]/70 backdrop-blur-sm" onClick={closeEnquiry} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl bg-[#fdfbf7] border border-[#d4af37]/40 shadow-2xl max-h-[92vh] overflow-y-auto"
          >
            <button onClick={closeEnquiry} data-testid="enquiry-close" className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center text-[#3b1a1a] hover:bg-[#7c1d1d] hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>

            {done ? (
              <div className="p-12 text-center" data-testid="enquiry-success">
                <CheckCircle2 className="w-16 h-16 text-[#14523d] mx-auto" />
                <h3 className="font-serif text-3xl text-[#3b1a1a] mt-5">Thank you!</h3>
                <p className="text-[#5a4b4b] mt-3 max-w-md mx-auto">
                  Your enquiry has reached our travel designers. We’ll craft a personalised itinerary and reply within 24 hours.
                </p>
                <button onClick={closeEnquiry} className="mt-8 bg-[#7c1d1d] text-white px-8 py-3 text-xs tracking-[0.24em] uppercase">Close</button>
              </div>
            ) : (
              <div className="p-7 sm:p-10">
                <div className="text-[11px] tracking-[0.34em] uppercase text-[#7c1d1d] font-semibold">Plan My Trip</div>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a] mt-2 leading-tight">
                  Let’s design your <span className="italic text-[#7c1d1d]">India journey</span>
                </h3>
                <p className="text-sm text-[#5a4b4b] mt-2">No prices, no obligation — just a tailored plan made for you.</p>

                <form onSubmit={onSubmit} data-testid="enquiry-form" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name *"><input data-testid="enq-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" placeholder="Your name" /></Field>
                  <Field label="Country">
                    <select data-testid="enq-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="inp">
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Email *"><input data-testid="enq-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="inp" placeholder="you@email.com" /></Field>
                  <Field label="Phone"><input data-testid="enq-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="inp" placeholder="+ country code" /></Field>
                  <Field label="WhatsApp"><input data-testid="enq-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="inp" placeholder="+ country code" /></Field>
                  <Field label="Travel Dates"><input data-testid="enq-dates" value={form.travel_dates} onChange={(e) => set("travel_dates", e.target.value)} className="inp" placeholder="e.g. Dec 2025, 10 days" /></Field>
                  <Field label="Adults"><input data-testid="enq-adults" type="number" min={1} value={form.adults} onChange={(e) => set("adults", e.target.value)} className="inp" /></Field>
                  <Field label="Children"><input data-testid="enq-children" type="number" min={0} value={form.children} onChange={(e) => set("children", e.target.value)} className="inp" /></Field>
                  <div className="sm:col-span-2"><Field label="Preferred Tour"><input data-testid="enq-tour" value={form.preferred_tour} onChange={(e) => set("preferred_tour", e.target.value)} className="inp" placeholder="e.g. Kashmir Paradise, Golden Triangle" /></Field></div>
                  <div className="sm:col-span-2"><Field label="Message"><textarea data-testid="enq-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className="inp resize-none" placeholder="Tell us about your dream trip..." /></Field></div>

                  <button type="submit" disabled={loading} data-testid="enq-submit" className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] disabled:opacity-60 text-white px-8 py-4 text-xs tracking-[0.26em] uppercase transition-colors border border-[#d4af37]/40">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {loading ? "Sending..." : "Send Enquiry"}
                  </button>
                  {settings?.recaptcha_site_key && (
                    <p className="sm:col-span-2 text-[10px] text-[#8a7c66] leading-relaxed">
                      Protected by reCAPTCHA — Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a> apply.
                    </p>
                  )}
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
